import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Package, Pencil, Trash2, MessageCircle, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/PageHeader";
import { Field, BigButton } from "@/components/Field";
import { AddButton, TextArea, Empty, ConfirmDelete } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

const BRL = (v) => (v == null ? "Consulte" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const fotoUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/produtos/${p.produto_id}/foto?v=${encodeURIComponent(p.updated_at || "")}`;
const EMPTY = { nome: "", descricao: "", preco: "", categoria: "", ativo: true };

const ProdutoDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const ref = useRef();
  useEffect(() => { if (open) { setForm(editing ? { ...EMPTY, ...editing, preco: editing.preco ?? "" } : EMPTY); setFile(null); } }, [open, editing]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const body = { nome: form.nome, descricao: form.descricao, categoria: form.categoria, ativo: form.ativo, preco: form.preco === "" ? null : Number(form.preco) };
    try {
      const { data } = editing ? await api.put(`/produtos/${editing.produto_id}`, body) : await api.post("/produtos", body);
      if (file) { const fd = new FormData(); fd.append("file", file); await api.post(`/produtos/${data.produto_id}/foto`, fd); }
      toast.success("Produto salvo");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="produto-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nome *" id="pr-nome" required value={form.nome} onChange={set("nome")} data-testid="produto-nome" />
          <Field label="Categoria" id="pr-cat" value={form.categoria || ""} onChange={set("categoria")} data-testid="produto-categoria" />
          <Field label="Preço (R$) — vazio = Consulte" id="pr-preco" type="number" step="0.01" min="0" value={form.preco} onChange={set("preco")} data-testid="produto-preco" />
          <TextArea label="Descrição" id="pr-desc" value={form.descricao || ""} onChange={set("descricao")} data-testid="produto-descricao" />
          <label className="flex items-center justify-between h-12"><span className="font-semibold">Visível para clientes</span>
            <Switch checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} data-testid="produto-ativo" /></label>
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="produto-foto-input" />
          <BigButton type="button" variant="outline" onClick={() => ref.current.click()} data-testid="produto-foto-button"><Camera className="h-5 w-5" /> <span className="truncate">{file ? file.name : "Escolher foto"}</span></BigButton>
          <BigButton type="submit" data-testid="produto-submit">Salvar</BigButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Card = ({ p, isAdmin, onEdit, onDelete, wpp }) => {
  const [err, setErr] = useState(false);
  const interest = () => {
    const msg = `Olá! Tenho interesse no produto "${p.nome}" (${BRL(p.preco)}) que vi no app Fazenda Digital.`;
    window.open(`https://wa.me/${wpp}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className={`bg-white rounded-3xl border border-[#E2DDD3] overflow-hidden flex flex-col fade-up ${p.ativo ? "" : "opacity-60"}`} data-testid={`produto-card-${p.produto_id}`}>
      <div className="aspect-[4/3] bg-[#F4F1EA] flex items-center justify-center">
        {p.tem_foto && !err ? <img src={fotoUrl(p)} alt={p.nome} onError={() => setErr(true)} className="h-full w-full object-cover" /> : <Package className="h-14 w-14 text-[#C9C2B4]" />}
      </div>
      <div className="p-5 flex flex-col flex-1">
        {p.categoria && <p className="text-[11px] font-bold uppercase tracking-wider text-[#C86D51]">{p.categoria}</p>}
        <h3 className="text-xl font-extrabold leading-tight mt-1">{p.nome}</h3>
        {p.descricao && <p className="text-sm text-[#5C6B66] mt-2 line-clamp-3 whitespace-pre-line">{p.descricao}</p>}
        <p className="font-display text-2xl font-extrabold text-[#1E4D2B] mt-3" data-testid={`produto-preco-${p.produto_id}`}>{BRL(p.preco)}</p>
        <div className="mt-auto pt-4">
          {isAdmin ? (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => onEdit(p)} className="h-12 rounded-xl border-2 border-[#E2DDD3] font-semibold flex items-center justify-center gap-2 hover:border-[#1E4D2B]" data-testid={`produto-edit-${p.produto_id}`}><Pencil className="h-4 w-4" /> Editar</button>
              <button onClick={() => onDelete(p)} className="h-12 rounded-xl border-2 border-[#E2DDD3] text-[#B05A40] font-semibold flex items-center justify-center gap-2" data-testid={`produto-delete-${p.produto_id}`}><Trash2 className="h-4 w-4" /> Excluir</button>
            </div>
          ) : (
            <button onClick={interest} className="h-14 w-full rounded-xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90" data-testid={`produto-interesse-${p.produto_id}`}>
              <MessageCircle className="h-5 w-5" /> Tenho interesse</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ProdutosPage() {
  const { user } = useAuth();
  const { whatsapp } = useSettings();
  const isAdmin = user.role === "admin";
  const { data, reload } = useFetch("/produtos");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);
  const wpp = (() => { const d = (whatsapp || "").replace(/\D/g, ""); return d.length === 10 || d.length === 11 ? `55${d}` : d; })();
  const remove = async () => { try { await api.delete(`/produtos/${del.produto_id}`); setDel(null); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="produtos-page">
      <PageHeader eyebrow="Loja" title="Produtos" subtitle={isAdmin ? "Catálogo exibido para os clientes." : "Produtos que a Fazenda Digital tem disponíveis para você."}
        action={isAdmin && <AddButton onClick={() => { setEditing(null); setOpen(true); }} data-testid="produto-add-button">Novo produto</AddButton>} />
      {isAdmin && !wpp && <p className="mb-4 text-sm text-[#B05A40]" data-testid="produto-wpp-warning">Cadastre o WhatsApp da empresa em Configurações para o botão "Tenho interesse".</p>}
      {data?.length === 0 && <Empty testId="produto-list-empty">Nenhum produto disponível no momento.</Empty>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="produto-list">
        {(data || []).map((p) => <Card key={p.produto_id} p={p} isAdmin={isAdmin} wpp={wpp} onEdit={(x) => { setEditing(x); setOpen(true); }} onDelete={setDel} />)}
      </div>
      <ProdutoDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={reload} />
      <ConfirmDelete open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Excluir produto?" description={del?.nome} onConfirm={remove} />
    </div>
  );
}
